'use client';
import { type DocumentNode } from '@apollo/client';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  compact,
  formatName,
  formatPrice,
  humanizeTier,
  IconCheck,
  IconQuestion,
  IconStopOctagonMinus,
  IconX,
  pluralize,
  Popover,
  ProfileImage,
  Select,
  type SelectOption,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { type FC, useEffect, useMemo } from 'react';

import {
  CoachVisibility,
  PackageVisibility,
  SkuTier,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import {
  getCoachUrl,
  getCTATooltipTextForBlockedUser,
} from '../../utils/coach';
import { SUBSCRIBER_DUMMY_COACH_URN } from '../../utils/constants';
import {
  getLelandPlusLandingPageUrl,
  isLiveLelandPlusCategory,
} from '../../utils/leland-plus';
import {
  CoachingTimeType,
  getCheapestOfferingPackagePrice,
  getOfferingPackageCoachingTimeLabel,
  offeringPackagePricesToPlanPrices,
} from '../../utils/package';
import { getTierDescription } from '../../utils/tier';
import { useLoginModal } from '../LoginModal';
import { useScheduleModal } from '../coach/modals/ScheduleModal';
import LelandExperienceGuarantee from '../common/LelandExperienceGuarantee';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import { OfferingPackageImage } from './OfferingPackageImage';
import {
  type OfferingPackagePricingInfoOfferingPackageFragment,
  type OfferingPackagePricingInfoOpportunityCoachFragment,
} from './__generated-gql-types__/OfferingPackagePricingInfo.generated';

interface OfferingPackagePricingInfoProps {
  blocked?: boolean;
  coachTier: SkuTier;
  coachView?: boolean;
  isLoading: boolean;
  offeringPackage: OfferingPackagePricingInfoOfferingPackageFragment;
  onMessageCoach: () => void;
  onPurchaseOrSelectTier: () => void;
  opportunityCoach?: Possible<OfferingPackagePricingInfoOpportunityCoachFragment>;
  refetchQueries?: DocumentNode[];
  setCoachTier: (tier: SkuTier) => void;
}

const OfferingPackagePricingInfo: FC<OfferingPackagePricingInfoProps> = ({
  blocked,
  coachTier,
  coachView,
  isLoading,
  offeringPackage,
  onPurchaseOrSelectTier,
  opportunityCoach,
  refetchQueries,
  setCoachTier,
}) => {
  const { isLexEnabled } = useLex();
  const isRampCoachSetPrice = isLexEnabled(LexSlug.RAMP_COACH_SET_PRICE);
  const styleAsCoachPackage = isRampCoachSetPrice
    ? opportunityCoach
    : !offeringPackage.isUniversalPackage;

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setScheduleModalOpen, Modal: ScheduleModal } =
    useScheduleModal();
  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();

  const { currentUser } = useAuth();
  const searchParams = useSearchParams();

  const coachTierQueryParam = searchParams.get(
    'coachTier',
  ) as Optional<SkuTier>;

  const isLoggedIn = currentUser?.applicant;

  const canWorkWithCoach = coachTier === opportunityCoach?.tier;

  const loginOrSetScheduleModalOpen = () => {
    if (isLoggedIn) {
      setScheduleModalOpen(true);
    } else {
      setLoginModalOpen(true);
    }
  };

  const availableTiers = useMemo(
    () => new Set(offeringPackage.prices?.map((price) => price.tier)),
    [offeringPackage.prices],
  );

  const pricesForSelectedCoachTier = useMemo(
    () =>
      offeringPackage.isUniversalPackage
        ? offeringPackage.prices?.filter((price) => price.tier === coachTier)
        : null,
    [coachTier, offeringPackage.isUniversalPackage, offeringPackage.prices],
  );

  const coachTierOptions: Array<SelectOption<SkuTier>> = useMemo(() => {
    return [
      availableTiers.has(SkuTier.ELITE_PLUS)
        ? {
            label: `${humanizeTier(SkuTier.ELITE_PLUS)}`,
            value: SkuTier.ELITE_PLUS,
            subTitle: getTierDescription(
              SkuTier.ELITE_PLUS,
              offeringPackage.category?.goal,
            ),
          }
        : null,
      availableTiers.has(SkuTier.ELITE)
        ? {
            label: `${humanizeTier(SkuTier.ELITE)}`,
            value: SkuTier.ELITE,
            subTitle: getTierDescription(
              SkuTier.ELITE,
              offeringPackage.category?.goal,
            ),
          }
        : null,
      availableTiers.has(SkuTier.PRO)
        ? {
            label: `${humanizeTier(SkuTier.PRO)}`,
            value: SkuTier.PRO,
            subTitle: getTierDescription(
              SkuTier.PRO,
              offeringPackage.category?.goal,
            ),
          }
        : null,
      availableTiers.has(SkuTier.BASE)
        ? {
            label: `${humanizeTier(SkuTier.BASE)}`,
            value: SkuTier.BASE,
            subTitle: getTierDescription(
              SkuTier.BASE,
              offeringPackage.category?.goal,
            ),
          }
        : null,
    ].filter(compact);
  }, [offeringPackage.category?.goal, availableTiers]);

  const pricesToUse = pricesForSelectedCoachTier ?? offeringPackage.prices;

  const offeringPackageCoachingTimeLabel = getOfferingPackageCoachingTimeLabel(
    pricesToUse,
    { compact: true, longFormatting: true },
  );

  const disabled = blocked || isLoading || !pricesToUse?.length;

  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    offeringPackage.coach.user.firstName,
  );

  const { minHours, maxHours } = useMemo(() => {
    const prices = offeringPackagePricesToPlanPrices(
      offeringPackage.prices ?? [],
    );

    let minHours: number | null = null;
    let maxHours: number | null = null;

    for (const price of prices) {
      if (price.tierType === CoachingTimeType.EXACT) {
        if (minHours === null || price.hours < minHours) {
          minHours = price.hours;
        }
        if (maxHours === null || price.hours > maxHours) {
          maxHours = price.hours;
        }
      }

      if (price.tierType === CoachingTimeType.ESTIMATE) {
        for (const tier of price.tiers) {
          if (minHours === null || tier.minHours < minHours) {
            minHours = tier.minHours;
          }
          if (maxHours === null || tier.maxHours > maxHours) {
            maxHours = tier.maxHours;
          }
        }
      }
    }

    return { minHours, maxHours };
  }, [offeringPackage.prices]);

  useEffect(() => {
    if (coachTierOptions.length === 1) {
      setCoachTier(coachTierOptions[0].value);
    }
  }, [coachTierOptions, setCoachTier]);

  const noContactForNewClient =
    opportunityCoach?.visibility === CoachVisibility.PRIVATE_CLOSED &&
    !opportunityCoach?.coachingRelationship &&
    opportunityCoach?.id !== SUBSCRIBER_DUMMY_COACH_URN;

  const isLelandPlusUser = !!currentUser?.applicant?.hasLelandPlusAccess;

  const lelandPlusUrl = getLelandPlusLandingPageUrl({
    isLelandPlusUser,
    categorySlug:
      offeringPackage &&
      isLiveLelandPlusCategory(offeringPackage?.category?.id ?? '')
        ? (offeringPackage?.category?.slug ?? null)
        : null,
    utmMedium: 'package-page',
  });

  return (
    <div
      className={`${offeringPackage.isUniversalPackage ? 'sm:rounded-xl sm:border' : 'rounded-xl border'} divide-y overflow-hidden bg-white`}
    >
      {offeringPackage.imageLink ? (
        <OfferingPackageImage
          containerClassName={`${offeringPackage.isUniversalPackage ? 'hidden sm:block ' : ''}w-full shrink-0`}
          sizes={{ dflt: '320px' }}
          name={offeringPackage.name}
          imageLink={offeringPackage.imageLink}
        />
      ) : null}
      {pricesToUse?.length ? (
        <div
          className={`grid w-full ${!styleAsCoachPackage ? 'justify-start p-4' : 'px-2 py-6 lg:px-6'}  ${
            offeringPackageCoachingTimeLabel &&
            !coachView &&
            styleAsCoachPackage
              ? 'grid-cols-2'
              : ''
          }`}
        >
          {coachView ? null : (
            <div
              className={`flex flex-col ${!styleAsCoachPackage ? 'items-start' : 'items-center'} justify-center whitespace-nowrap`}
            >
              <h3 className="text-base font-normal text-leland-gray-light">
                {offeringPackage.prices?.length === 1
                  ? 'Total price'
                  : 'Starting at'}
              </h3>
              <p className="text-xl font-medium text-leland-gray-dark">
                {formatPrice(getCheapestOfferingPackagePrice(pricesToUse) ?? 0)}
              </p>
            </div>
          )}
          {offeringPackageCoachingTimeLabel && styleAsCoachPackage ? (
            <div className="flex flex-col items-center justify-center">
              <h3 className="text-base font-normal text-leland-gray-light">
                Coaching time
              </h3>
              <p className="text-xl font-medium text-leland-gray-dark">
                {offeringPackageCoachingTimeLabel}
              </p>
            </div>
          ) : null}
          {!styleAsCoachPackage ? (
            <div className="flex flex-col py-4 text-leland-gray-light">
              {minHours && maxHours && minHours === maxHours ? (
                <div className="flex gap-2">
                  <IconCheck className="mt-1 size-4 shrink-0 text-leland-gray-extra-light" />
                  <span>{`${pluralize(minHours, 'hour')} of coaching`}</span>
                </div>
              ) : null}
              <div className="flex gap-2">
                <IconCheck className="mt-1 size-4 shrink-0 text-leland-gray-extra-light" />
                <span>Usable with any coach in the same tier</span>
              </div>
              {maxHours !== null && maxHours > 10 ? (
                <div className="flex gap-2">
                  <IconCheck className="mt-1 size-4 shrink-0 text-leland-gray-extra-light" />
                  <span>
                    Sizes of 10+ hours include{' '}
                    <Link
                      className="underline"
                      target="_blank"
                      href={lelandPlusUrl}
                      id="offering_package_pricing_info--leland_plus_info"
                    >
                      <Tooltip content="A library of videos, templates, and examples from top coaches">
                        <span className="underline">Leland+</span>
                      </Tooltip>
                    </Link>
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="w-full space-y-3 px-4 py-6">
        {offeringPackage.isUniversalPackage &&
        !styleAsCoachPackage &&
        !coachView ? (
          <div className="flex w-full flex-col gap-3">
            {coachTierQueryParam &&
            offeringPackage.visibility === PackageVisibility.PUBLIC ? (
              <div className="flex w-full">
                <h4 className="grow font-medium">
                  Purchasing on the{' '}
                  <Popover
                    content={getTierDescription(
                      coachTier,
                      offeringPackage.category?.goal,
                    )}
                  >
                    <span className="underline underline-offset-2">
                      {humanizeTier(coachTier)}
                    </span>
                  </Popover>{' '}
                  tier
                </h4>
              </div>
            ) : (
              <div className="w-full space-y-3">
                <div className="flex">
                  <h4 className="grow font-medium">Select a coach tier</h4>
                  <Popover
                    content={
                      <div className="max-w-[90vw]">
                        We’ve organized our coaches into tiers based on their
                        experience. You will only be able to use your time
                        balance with coaches in the tier you select.
                      </div>
                    }
                  >
                    <IconQuestion className="size-4 text-leland-gray-extra-light" />
                  </Popover>
                </div>
                {coachTierOptions.length > 1 ? (
                  <Select<SkuTier>
                    onValueChange={(value) => {
                      setCoachTier(value);
                    }}
                    options={coachTierOptions}
                    value={coachTier}
                  />
                ) : (
                  coachTierOptions.length === 1 && (
                    <div className="bg-leland-gray-hover p-2">
                      <p>{coachTierOptions[0].label}</p>
                      <p className="text-leland-gray-extra-light">
                        {coachTierOptions[0].subTitle}
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
            {opportunityCoach ? (
              <div className="group flex items-center gap-2 rounded-md bg-leland-gray-hover p-2">
                <div className="flex items-center">
                  <ProfileImage {...opportunityCoach.user} className="size-5" />
                  <div className="-m-0.5 rounded-full bg-leland-gray-hover p-1">
                    {canWorkWithCoach ? (
                      <IconCheck className="size-3 text-leland-primary" />
                    ) : (
                      <IconX className="size-3 text-leland-red" />
                    )}
                  </div>
                </div>
                <p className="grow text-leland-gray-light">
                  You {canWorkWithCoach ? 'can' : 'cannot'} work with{' '}
                  <Link
                    className="underline underline-offset-2"
                    href={getCoachUrl({
                      coachSlug: opportunityCoach.slug,
                      categorySlug: offeringPackage?.category?.slug,
                    })}
                    id="offering_package_pricing_info--opportunity_coach_link"
                  >
                    {formatName(opportunityCoach.user)}
                  </Link>{' '}
                  in this tier.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        {noContactForNewClient ? (
          <div className="flex items-center space-x-2 rounded-lg border p-4">
            <IconStopOctagonMinus className="size-6 shrink-0" />
            <div className="ml-3 leading-none text-leland-gray-dark">
              <p className="text-lg font-medium leading-none">
                Not currently taking new clients
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-center space-y-4">
            <Button
              buttonColor={ButtonColor.PRIMARY}
              disabled={!coachView && disabled}
              label={
                coachView
                  ? 'View package sizes'
                  : offeringPackage.prices?.length === 1 ||
                      pricesForSelectedCoachTier?.length === 1
                    ? 'Purchase'
                    : offeringPackage.isUniversalPackage && !styleAsCoachPackage
                      ? 'Select a package size'
                      : 'Select a size'
              }
              tooltipContent={
                coachView
                  ? undefined
                  : blocked
                    ? blockedUserCTAText
                    : pricesToUse?.length
                      ? undefined
                      : 'There are no available options for this tier.  Please contact support@joinleland.com for more information.'
              }
              onClick={onPurchaseOrSelectTier}
              size={ButtonSize.LARGE}
              width={ButtonWidth.FULL}
              id="offering_package_pricing_info--purchase_or_select_size"
            />
            {!!offeringPackage.coach.coachingRelationship ||
            (offeringPackage.isUniversalPackage &&
              !styleAsCoachPackage) ? null : (
              <>
                <Button
                  label="Schedule a free intro call"
                  width={ButtonWidth.FULL}
                  size={ButtonSize.LARGE}
                  disabled={disabled}
                  tooltipContent={blocked ? blockedUserCTAText : null}
                  onClick={() => loginOrSetScheduleModalOpen()}
                  id="offering_package_pricing_info--schedule_intro_call"
                />
                {opportunityCoach ? (
                  <ScheduleModal
                    coach={opportunityCoach}
                    categoryUrn={offeringPackage.category?.id ?? null}
                    categorySlug={offeringPackage.category?.slug ?? null}
                    refetchQueries={refetchQueries}
                    onSuccessModalDismiss={openPhoneNumberModalIfNecessary}
                  />
                ) : null}
                <PhoneNumberModal />
              </>
            )}
            <LelandExperienceGuarantee className="mt-3 justify-center" />
            <LoginModal />
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferingPackagePricingInfo;
